/**
 * 共通部品 (components/common/) の文言 — 英語。
 * 形は `commonJa` と同じでなければならない (TranslationOf がキーの欠落・余分をエラーにする)。
 */
import type { TranslationOf } from '../../types';
import type { commonJa } from '../ja/common';

export const commonEn: TranslationOf<typeof commonJa> = {
  fileNaming: {
    original: 'Original name: {name}',
    manual: 'Renamed manually',
    unreadable: 'Could not read this file',
    succeeded: 'Named from transaction dates',
    pending: 'Reading the file name',
    read: 'Name bank documents from their dates',
    queued: '{count} files queued. Reload after a short wait.',
    readFailed: 'Could not start reading file names. Reload and try again.',
    hint: 'Read unprocessed or unreadable bank documents. Manually changed names are preserved (up to 20 files per request).',
    rename: 'Rename file',
    zip: 'Save selected files as ZIP',
    zipFailed: 'Could not create the ZIP. Select fewer files or download them individually.',
  },
  actions: {
    cancel: 'Cancel',
    confirm: 'Confirm',
    close: 'Close',
    search: 'Search',
    loadMore: 'Load more',
  },
  status: {
    loading: 'Loading',
    loadingEllipsis: 'Loading...',
    searching: 'Searching...',
  },
  empty: {
    noData: 'No data',
  },
  pagination: {
    previous: 'Previous',
    next: 'Next',
  },
  table: {
    selectAllVisible: 'Select all visible rows',
    selectRow: 'Select this row',
  },
  search: {
    placeholder: 'Search...',
  },
  /** 一覧の「自分の担当 / すべて」切替 (my-assigned-default-view-20261005 / ADR-108)。 */
  assignedView: {
    groupLabel: 'Show',
    mine: 'Assigned to me',
    all: 'All',
    mineTitle: 'Show only items where you are the assigned lawyer or staff',
    allTitle: 'Show everything you can view',
    emptyMine: 'Nothing assigned to you matches.',
    showAll: 'Show all',
  },
  filter: {
    advanced: 'Advanced filters',
  },
  select: {
    placeholder: 'Select',
    searchPlaceholder: 'Search',
    noOptions: 'No options',
    noMatch: 'No matching options',
    moreAvailable: 'More options are available. Refine your search or load more.',
    clearSelection: 'Clear selection',
    previewScrollHint: 'Use Page Up / Page Down to scroll the full text',
  },
  text: {
    showAll: 'Show all',
    collapse: 'Collapse',
  },
  date: {
    unparsable: 'Could not read this as a date (e.g. 2026/08/18)',
  },
  toast: {
    clickToOpenCase: 'Click to open the case',
    clickToAction: 'Click to {action}',
  },
  knownMessages: {
    forbiddenAccess: 'You do not have permission to access this.',
    forbiddenOperation: 'You do not have permission to perform this operation.',
    sessionExpired: 'Your session has expired. Please sign in again.',
    requestAborted: 'The request was cancelled.',
    requestTimedOut: 'The request timed out ({seconds} s).',
    copyFailed: 'Could not copy.',
  },
  labels: {
    notSet: 'Not set',
    required: 'Required',
    optional: 'Optional',
    count: 'Count',
    itemCount: 'Items: {count}',
    bulkActions: 'Bulk actions',
    yes: 'Yes',
    no: 'No',
  },
  commonActions: {
    save: 'Save',
    saving: 'Saving...',
    delete: 'Delete',
    edit: 'Edit',
    back: 'Back',
    reload: 'Reload',
    download: 'Download',
    upload: 'Upload',
  },
  shared: {
    assignee: {
      unset: 'Unassigned',
      unresolved: 'Assignee unavailable',
    },
    notSet: 'Not set',
  },
  filePreview: {
    dialogLabel: 'Preview of {fileName}',
    zoomLevel: 'Zoom level',
    fitToScreen: 'Fit to screen',
    fitShort: 'Fit',
    rotate: 'Rotate',
    rotateLeft: 'Rotate left 90 degrees',
    rotateRight: 'Rotate right 90 degrees',
    rotateLeftShort: 'Rotate left',
    rotateRightShort: 'Rotate right',
    reset: 'Reset',
    resetView: 'Reset view',
    resetTitle: 'Reset zoom and rotation',
    rename: 'Rename file',
    renameShort: 'Rename',
    controls: 'Preview controls',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fetchFailed: 'Could not load the file',
    imageFailed: 'Could not display the image here',
    openInNewTab: 'Open in a new tab',
    unsupported: 'This file type cannot be previewed',
    downloadToView: 'Download to view',
    metaSeparator: ' · ',
  },
  bankAccount: {
    institution: 'Financial institution',
    institutionPlaceholder: 'e.g. Japan Post Bank',
    branch: 'Branch name',
    branchPlaceholder: 'e.g. Shinjuku branch',
    accountType: 'Account type',
    accountTypeNone: 'Not selected',
    accountTypes: {
      ordinary: 'Ordinary',
      current: 'Checking',
      savings: 'Savings',
      other: 'Other',
    },
    accountNumber: 'Account number',
    accountNumberPlaceholder: 'e.g. 1234567 (Japan Post Bank: symbol-number)',
    accountNumberHint:
      'Enter digits and hyphens only. Full-width characters and spaces are corrected automatically.',
  },
  businessDay: {
    placeholder: 'Select a date',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    dayUnavailable: '{date} (not available)',
  },
  stickySave: {
    region: 'Unsaved changes',
    title: 'You have unsaved changes',
    description:
      'Your edits on this page are not saved yet. When you save, they apply to the client portal, notifications, and messages.',
    note: 'Note: Saving applies to all message settings (every section above).',
    save: 'Save all message settings',
    discard: 'Discard changes',
    changeCount: 'Changes: {count}',
  },
  fileRename: {
    title: 'Rename file',
    label: 'File name',
    placeholder: 'File name (without extension)',
    hint: 'You cannot change the extension. Only the display name changes; the file content stays the same.',
  },
  fileUpload: {
    tooLarge: 'The file is larger than the limit ({maxSizeMB} MB)',
    failed: 'Upload failed',
    uploading: 'Uploading...',
    dropHint: 'Drag and drop, or click to choose a file',
    maxSize: 'Max {maxSizeMB} MB',
  },
  cloudSign: {
    draft: 'Draft',
    sent: 'Awaiting signature',
    signed: 'Signed',
    canceled: 'Canceled',
    template: 'Template',
    imported: 'Imported document',
    unknown: 'Unknown',
  },
};
